import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Calendar, Clock, MapPin, User, Phone, Mail, 
  Loader2, Check, X, RefreshCw, Eye, Filter
} from 'lucide-react';

const STATUT_COLORS = {
  demandee: 'bg-yellow-100 text-yellow-800',
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
  reprogrammee: 'bg-blue-100 text-blue-800',
  realisee: 'bg-purple-100 text-purple-800',
  archivee: 'bg-gray-100 text-gray-800'
};

const STATUT_LABELS = {
  demandee: 'Demandée',
  validee: 'Validée',
  refusee: 'Refusée',
  reprogrammee: 'Reprogrammée',
  realisee: 'Réalisée',
  archivee: 'Archivée'
};

export default function Visites() {
  const { token } = useAuth();
  const [visites, setVisites] = useState([]);
  const [sites, setSites] = useState([]);
  const [logements, setLogements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedVisite, setSelectedVisite] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [visitesRes, sitesRes, logementsRes] = await Promise.all([
        fetch(`${API_URL}/api/visites`, { headers }),
        fetch(`${API_URL}/api/sites`),
        fetch(`${API_URL}/api/logements`)
      ]);

      if (visitesRes.ok) setVisites(await visitesRes.json());
      if (sitesRes.ok) setSites(await sitesRes.json());
      if (logementsRes.ok) setLogements(await logementsRes.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatut = async (visiteId, newStatut) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/visites/${visiteId}/statut?statut=${newStatut}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur');
      
      toast.success(`Visite ${STATUT_LABELS[newStatut].toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleReprogrammer = async () => {
    if (!selectedVisite || !newDate) return;
    
    setUpdating(true);
    try {
      // Update date first
      await fetch(`${API_URL}/api/visites/${selectedVisite.visite_id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date_visite: newDate })
      });
      
      // Then update status
      await fetch(`${API_URL}/api/visites/${selectedVisite.visite_id}/statut?statut=reprogrammee`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Visite reprogrammée');
      setDialogOpen(false);
      setSelectedVisite(null);
      setNewDate('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la reprogrammation');
    } finally {
      setUpdating(false);
    }
  };

  const getItemName = (visite) => {
    if (visite.type_visite === 'SITE') {
      const site = sites.find(s => s.site_id === visite.site_id);
      return site ? `Site - ${site.nom || site.ville}` : 'Site inconnu';
    } else {
      const logement = logements.find(l => l.logement_id === visite.logement_id);
      return logement ? `Logement - ${logement.titre || logement.type_contrat}` : 'Logement inconnu';
    }
  };

  const filteredVisites = visites.filter(v => {
    if (filterStatut !== 'all' && v.statut !== filterStatut) return false;
    if (filterType !== 'all' && v.type_visite !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="visites-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-serif">Gestion des visites</h1>
              <p className="text-muted-foreground">{visites.length} visite(s) au total</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3">
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(STATUT_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="SITE">Sites</SelectItem>
                <SelectItem value="LOGEMENT">Logements</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {visites.filter(v => v.statut === 'demandee').length}
              </div>
              <div className="text-sm text-muted-foreground">En attente</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {visites.filter(v => v.statut === 'validee').length}
              </div>
              <div className="text-sm text-muted-foreground">Validées</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {visites.filter(v => v.statut === 'reprogrammee').length}
              </div>
              <div className="text-sm text-muted-foreground">Reprogrammées</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {visites.filter(v => v.statut === 'realisee').length}
              </div>
              <div className="text-sm text-muted-foreground">Réalisées</div>
            </CardContent>
          </Card>
        </div>

        {/* Visites Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des visites</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredVisites.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucune visite trouvée</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Bien</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVisites.map(visite => (
                    <TableRow key={visite.visite_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          {new Date(visite.date_visite).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          {getItemName(visite)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          {visite.client_nom || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          {visite.client_telephone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {visite.client_telephone}
                            </div>
                          )}
                          {visite.client_email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {visite.client_email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUT_COLORS[visite.statut]}>
                          {STATUT_LABELS[visite.statut]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {visite.statut === 'demandee' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600"
                                onClick={() => handleUpdateStatut(visite.visite_id, 'validee')}
                                disabled={updating}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleUpdateStatut(visite.visite_id, 'refusee')}
                                disabled={updating}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedVisite(visite);
                                  setDialogOpen(true);
                                }}
                                disabled={updating}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {visite.statut === 'validee' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-purple-600"
                              onClick={() => handleUpdateStatut(visite.visite_id, 'realisee')}
                              disabled={updating}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Terminée
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Reprogrammer Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reprogrammer la visite</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nouvelle date et heure</Label>
                <Input
                  type="datetime-local"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleReprogrammer} disabled={updating || !newDate}>
                {updating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Reprogrammer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
